import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TutorDashboardMockup } from '@/components/TutorDashboardMockup';
import { StudentRosterSection } from '@/components/StudentRosterSection';
import { TutorSchedulingSection } from '@/components/TutorSchedulingSection';
import { PaymentsFeatureSection } from '@/components/PaymentsFeatureSection';
import { CourseHookStrip } from '@/components/marketing/CourseHookStrip';
import { RoleAnswers, RoleClose, RoleHead } from '@/components/marketing/roleKit';
import { MARKETING_CTAS } from '@/lib/marketingCtas';

const CONTACT = 'mailto:admin@mentaratutors.com';

export const TUTORS_ANSWERS = [
  {
    q: 'How do I get a tutor account?',
    a: (
      <>
        Tutor accounts are created by the Mentara team. Email{' '}
        <a href={CONTACT} className="underline underline-offset-4">admin@mentaratutors.com</a> with the subjects you teach.
      </>
    ),
  },
  {
    q: 'Where do students come from?',
    a: 'Students request a subject and Mentara matches each request with an available tutor. Students can also ask for a tutor they have already worked with.',
  },
  {
    q: 'How do I get paid?',
    a: 'Students pay by card through Stripe. Your payments page shows each session, its status and your share. Payouts are arranged with the Mentara team.',
  },
  {
    q: 'Who can see my chats?',
    a: 'You, your student, and a linked parent if the student allows it. A Mentara admin is a read-only observer in every student and tutor chat.',
  },
  {
    q: 'What has the SAT course got to do with me?',
    a: 'The course is free for students and needs no account. For students you teach, you can see their SAT results and assign a diagnostic or a practice set.',
  },
] as const;

export const TutorsLandingPage = () => (
  <>
    <RoleHead
      title="Grow Your Tutoring Business | Tutor Management Software | Mentara"
      description="Run your tutoring in one place: requests, scheduling, your students and session payments. Your SAT students can start with Mentara's free SAT course."
      keywords="tutoring business, tutor management software, student management, scheduling, session payments, tutor tools, free SAT course"
      path="/what-we-do/tutors/"
    />

    {/* Starting Line: asymmetric headline, then the tutor's console running wide. */}
    <section aria-labelledby="tutors-title" className="overflow-hidden border-b border-sky-700/20 bg-sky-700/[0.04] dark:border-sky-400/20">
      <div className="mx-auto w-full max-w-6xl px-4 pt-10 sm:px-6 lg:pt-14">
        <div className="grid items-end gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-16">
          <h1 id="tutors-title" className="text-balance text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
            Teach. We handle the admin.
          </h1>
          <div className="flex flex-col items-start gap-5 pb-1">
            <p className="text-pretty text-lg leading-relaxed text-muted-foreground">
              Requests, agreed times, your students and each session's payment, in one dashboard. Meeting links are made for you.
            </p>
            <Button asChild size="lg" className="h-12 w-full px-8 text-base sm:w-auto">
              <Link to={MARKETING_CTAS.tutors.href}>{MARKETING_CTAS.tutors.label}</Link>
            </Button>
          </div>
        </div>
        <figure className="mx-auto mt-10 max-w-5xl">
          <div
            className="max-h-[20rem] overflow-hidden rounded-t-2xl border border-b-0 border-sky-700/20 bg-background [mask-image:linear-gradient(to_bottom,black_70%,transparent)] sm:max-h-[24rem]"
            data-testid="hero-artifact"
          >
            <TutorDashboardMockup />
          </div>
          <figcaption className="py-3 text-sm text-muted-foreground">Example tutor dashboard.</figcaption>
        </figure>
      </div>
    </section>

    <CourseHookStrip audience="tutors" />
    <StudentRosterSection />
    <TutorSchedulingSection />
    <PaymentsFeatureSection audience="tutors" />
    <RoleAnswers audience="tutors" answers={TUTORS_ANSWERS} />
    <RoleClose
      audience="tutors"
      heading="Bring your subjects. We bring the students."
      body="Tutor accounts are set up by the Mentara team. Start here, or email us."
      primary={MARKETING_CTAS.tutors}
      secondary={{ label: 'Email us', to: CONTACT, external: true }}
    />
  </>
);
